import React, { useState } from 'react';
import { apiFetch, useError } from '../../admin-page/apis/api';
import { isReadOnlyPreviewEnabled } from '../../../utils/readOnlyPreview';

const splitName = (name) => {
  const parts = name.trim().split(/\s+/);

  return parts.length > 1
    ? { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1] }
    : { first: parts[0], last: '' };
};

const ParentNameEditor = ({ name = '', onNameChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [savedForm, setSavedForm] = useState(() => splitName(name));
  const [form, setForm] = useState(savedForm);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const { checkError } = useError();
  const isReadOnly = isReadOnlyPreviewEnabled();

  const handleSave = async (event) => {
    event.preventDefault();
    if (isSaving || isReadOnly) return;

    const first = form.first.trim();
    const last = form.last.trim();
    if (!first || !last) {
      setError('Enter your first and last name.');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      await apiFetch(`${process.env.REACT_APP_API_URL}/api/name`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ first, last }),
      });

      const updatedName = `${first} ${last}`;
      const previousName = localStorage.getItem('name')?.trim();
      const previousSurveyKey = previousName
        ? `parentPostSurveySubmitted:${previousName}`
        : 'parentPostSurveySubmitted';
      if (localStorage.getItem(previousSurveyKey) === 'true') {
        localStorage.setItem(`parentPostSurveySubmitted:${updatedName}`, 'true');
      }
      localStorage.setItem('name', updatedName);
      setSavedForm({ first, last });
      onNameChange(updatedName);
      setIsEditing(false);
    } catch (saveError) {
      checkError(saveError);
      setError('Unable to update your name. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className='z-card-gray profile-name-editor'>
      <h2>Information</h2>
      {isEditing ? (
        <form onSubmit={handleSave} aria-busy={isSaving}>
          <p>Check your first and last name before saving.</p>
          <div className='profile-name-fields'>
            <label>
              First:
              <input
                name='first'
                autoComplete='given-name'
                required
                autoFocus
                disabled={isSaving || isReadOnly}
                value={form.first}
                onChange={(event) => {
                  setForm({ ...form, first: event.target.value });
                  setError('');
                }}
              />
            </label>
            <label>
              Last:
              <input
                name='last'
                autoComplete='family-name'
                required
                disabled={isSaving || isReadOnly}
                value={form.last}
                onChange={(event) => {
                  setForm({ ...form, last: event.target.value });
                  setError('');
                }}
              />
            </label>
          </div>
          <div className='profile-name-actions'>
            <button type='submit' disabled={isSaving || isReadOnly}>
              {isSaving ? 'Saving…' : 'Save name'}
            </button>
            <button
              type='button'
              className='profile-name-secondary'
              disabled={isSaving}
              onClick={() => {
                setIsEditing(false);
                setError('');
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className='profile-name-display'>
          <dl>
            <div>
              <dt>First:</dt>
              <dd>{savedForm.first || '—'}</dd>
            </div>
            <div>
              <dt>Last:</dt>
              <dd>{savedForm.last || '—'}</dd>
            </div>
          </dl>
          {!isReadOnly && (
            <button
              type='button'
              className='profile-name-edit-button'
              onClick={() => {
                setForm(savedForm);
                setError('');
                setIsEditing(true);
              }}
            >
              Edit
            </button>
          )}
        </div>
      )}
      {error && <p role='alert'>{error}</p>}
    </div>
  );
};

export default ParentNameEditor;
